import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assignment as ReturnsIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  FactCheck as OperationsIcon,
  Assessment as ReportsIcon,
} from '@mui/icons-material';

const drawerWidth = 200; // Reduced from 240 to maximize content space

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'All Returns', icon: <ReturnsIcon />, path: '/returns' },
  { text: 'Operations', icon: <OperationsIcon />, path: '/operations' },
  { text: 'Reports', icon: <ReportsIcon />, path: '/reports' },
  { text: 'New Return', icon: <AddIcon />, path: '/returns/new' },
];

const settingsItems = [
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings', adminOnly: true },
  { text: 'User Audit Log', icon: <SecurityIcon />, path: '/admin/audit-log', adminOnly: true },
];

const Sidebar = ({ open, onClose, user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [apiStatus, setApiStatus] = useState({
    amazon: { status: 'loading', message: 'Checking...' },
    sellercloud: { status: 'loading', message: 'Checking...' },
    ebay: { status: 'loading', message: 'Checking...' }
  });
  // The deployed build, shown at the foot of the column so anyone can tell which version
  // is live without shelling into the server.
  const [build, setBuild] = useState(null);

  const handleNavigation = (path) => {
    navigate(path);
  };

  const fetchBuildInfo = async () => {
    try {
      const response = await axios.get('/api/version');
      if (response.data?.success) setBuild(response.data);
    } catch (error) {
      // Version display is a convenience, never a failure worth surfacing.
      console.error('Error fetching build info:', error);
    }
  };

  const fetchAPIStatus = async () => {
    try {
      const response = await axios.get('/api/status/apis');
      if (response.data.success) {
        setApiStatus(response.data.data.apis);
      }
    } catch (error) {
      console.error('Error fetching API status:', error);
      setApiStatus({
        amazon: { status: 'error', message: 'Connection failed' },
        sellercloud: { status: 'error', message: 'Connection failed' },
        ebay: { status: 'error', message: 'Connection failed' }
      });
    }
  };

  useEffect(() => {
    fetchAPIStatus();
    const interval = setInterval(fetchAPIStatus, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Fetched once. The build cannot change while the server process is alive, and a deploy
  // restarts it, so there is nothing to poll.
  useEffect(() => {
    fetchBuildInfo();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return '#38A169'; // Green
      case 'loading': return '#D69E2E'; // Orange/Amber
      case 'error':
      case 'disconnected':
      default: return '#E53E3E'; // Red
    }
  };

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={open}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          borderRight: '2px solid #A0AEC0',
          backgroundColor: '#FFFFFF',
          boxShadow: '2px 0 8px rgba(26, 54, 93, 0.1)',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
        <Box sx={{ p: 1.5, mt: 7 }}> {/* Account for navbar */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 1.5, bgcolor: '#F7FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
            <img 
              src="https://www.xs-stock.co.uk/cdn/shop/files/XS_NEW_LOGO_2ac57727-dd78-4de8-bd11-0c0bb84a6858.png?v=1738612353&width=400" 
              alt="XS Stock"
              style={{
                height: '24px',
                marginRight: '8px'
              }}
            />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1A365D' }}>
              RETURNS PORTAL
            </Typography>
          </Box>
        </Box>
        
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                selected={location.pathname === item.path}
                sx={{
                  mx: 1,
                  borderRadius: 2,
                  '&.Mui-selected': {
                    backgroundColor: '#1A365D',
                    color: 'white',
                    boxShadow: '0 2px 8px rgba(26, 54, 93, 0.3)',
                    '&:hover': {
                      backgroundColor: '#2D4A66',
                    },
                    '& .MuiListItemIcon-root': {
                      color: '#CBD5E0',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{ 
                    color: location.pathname === item.path ? 'white' : 'text.secondary' 
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {settingsItems.some(item => !item.adminOnly || user?.role === 'admin') && (
          <>
            <Divider sx={{ my: 2 }} />

            <Box sx={{ px: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                SYSTEM
              </Typography>
            </Box>

            <List>
              {settingsItems.filter(item => !item.adminOnly || user?.role === 'admin').map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    selected={location.pathname === item.path}
                    sx={{
                      mx: 1,
                      borderRadius: 2,
                      '&.Mui-selected': {
                        backgroundColor: '#1A365D',
                        color: 'white',
                        boxShadow: '0 2px 8px rgba(26, 54, 93, 0.3)',
                        '&:hover': {
                          backgroundColor: '#2D4A66',
                        },
                        '& .MuiListItemIcon-root': {
                          color: '#CBD5E0',
                        },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{ 
                        color: location.pathname === item.path ? 'white' : 'text.secondary' 
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}

        {/* Status indicators at bottom */}
        <Box sx={{ 
          mt: 'auto', 
          p: 2, 
          bgcolor: '#1A365D',
          borderTop: '2px solid #A0AEC0',
          borderRadius: '12px 12px 0 0',
          mx: 1,
          mb: 1
        }}>
          <Typography variant="caption" sx={{ color: '#CBD5E0', fontWeight: 600 }}>
            API STATUS
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box 
                sx={{ 
                  width: 10, 
                  height: 10, 
                  borderRadius: '50%', 
                  bgcolor: getStatusColor(apiStatus.amazon?.status),
                  mr: 1,
                  boxShadow: `0 0 8px ${getStatusColor(apiStatus.amazon?.status)}66`
                }} 
              />
              <Typography variant="caption" sx={{ color: 'white', fontSize: '0.75rem' }}>Amazon</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box 
                sx={{ 
                  width: 10, 
                  height: 10, 
                  borderRadius: '50%', 
                  bgcolor: getStatusColor(apiStatus.sellercloud?.status),
                  mr: 1,
                  boxShadow: `0 0 8px ${getStatusColor(apiStatus.sellercloud?.status)}66`
                }} 
              />
              <Typography variant="caption" sx={{ color: 'white', fontSize: '0.75rem' }}>Sellercloud</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box 
                sx={{ 
                  width: 10, 
                  height: 10, 
                  borderRadius: '50%', 
                  bgcolor: getStatusColor(apiStatus.ebay?.status),
                  mr: 1,
                  boxShadow: `0 0 8px ${getStatusColor(apiStatus.ebay?.status)}66`
                }} 
              />
              <Typography variant="caption" sx={{ color: 'white', fontSize: '0.75rem' }}>eBay</Typography>
            </Box>
          </Box>
        </Box>
        </Box>

        {/* Live build reference, pinned to the foot of the column. */}
        <Box
          sx={{
            mt: 'auto',
            px: 1.5,
            py: 1,
            borderTop: '1px solid #E2E8F0',
            bgcolor: '#F7FAFC',
          }}
          title={build
            ? [build.commitSubject, build.commitDate, `branch ${build.branch}`,
               build.dirty ? 'working tree has uncommitted changes' : null]
                .filter(Boolean).join('\n')
            : 'Loading build information'}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: '#718096',
              fontSize: '0.68rem',
              lineHeight: 1.35,
              wordBreak: 'break-word',
            }}
          >
            {build ? (
              <>
                <span style={{ fontWeight: 600 }}>v{build.version}</span>
                {' \u00b7 '}
                {build.branch || 'detached'}#{build.commit || 'unknown'}
                {build.dirty ? ' \u00b7 modified' : ''}
              </>
            ) : (
              'version unavailable'
            )}
          </Typography>
          {build && build.commitDate && (
            <Typography
              variant="caption"
              sx={{ display: 'block', color: '#A0AEC0', fontSize: '0.62rem', lineHeight: 1.3 }}
            >
              built {new Date(build.commitDate).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric'
              })}
            </Typography>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
